import { useMutation, useQueryClient } from "@tanstack/react-query";
import { configuration } from "../../../configuration";
import { useSignalR } from "../../../hooks";
import { CreateEditBlockResultDto, EditBlockDto, FetchError } from "../../types";
import { useAccessToken } from "../../use-access-token";
import { queryKeys } from "../queryKeys";

type Variables = {
  planGuid: string;
  guid: string;
  name: string;
  hintText: string;
  sortOrder: string;
  modifiedByUserId: string;
};

const useEditBlockRequest = () => {
  const { getAccessToken } = useAccessToken();

  async function editBlockRequest(variables: Variables): Promise<CreateEditBlockResultDto> {
    const accessToken = await getAccessToken();
    const body: EditBlockDto = {
      name: variables.name,
      hintText: variables.hintText,
      sortOrder: variables.sortOrder,
      modifiedByUserId: variables.modifiedByUserId,
    };

    const response = await fetch(`${configuration.apiRootUrl}/blocks/${variables.guid}`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      method: "put",
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new FetchError(response);
    }

    return response.json();
  }

  return { editBlockRequest };
};

const useEditBlock = () => {
  const { editBlockRequest } = useEditBlockRequest();
  const queryClient = useQueryClient();
  const signalR = useSignalR();

  return useMutation({
    mutationFn: editBlockRequest,
    onSuccess: (_data, variables) => {
      signalR.sendRefetchPlan(variables.planGuid);
    },
    onSettled: (_data, _error, variables, _context) => {
      queryClient.invalidateQueries(queryKeys.plans.getPlan(variables.planGuid));
      queryClient.invalidateQueries(queryKeys.plans.getStructure(variables.planGuid));
      queryClient.invalidateQueries(queryKeys.plans.getPlanSubitems(variables.planGuid));
    },
  });
};

export { useEditBlock };
